import React, {
  useRef, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import useInterval from '@use-it/interval';
import { animated, useSpring } from 'react-spring';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';

import BoardData from './BoardData';
import LeaderboardSummaryTable from './LeaderboardSummaryTable';

import SubText from '../../../shared/_TBComponents/SubText';
import Button from '../../../shared/_TBComponents/Button';
import TBTooltip from '../../../shared/_TBComponents/Tooltip';

import { setModalOpen, reloadLeaderboard, closeBoard as closeBoardAction } from '../ducks/leaderboardActions';

import iconSet from '../../../shared/images/teambuildr-selection.json';

const BoardContainer = styled('div')`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => (props.theme.colors.background[0])};
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  flex: 1;
  margin-right: 15px;
  min-width: 0;
  min-height: 1;
  :last-of-type {
    margin-right: 0px;
  }
`;

const BoardLoadingBar = styled('div')`
  height: 4px;
  background-color: orange;
  width: 100%;
`;

const BoardHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 20px 25px;
  align-items: center;
`;

const ExerciseNameWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const InfoIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid #444444;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
  line-height: 0;
`;

const MenuWrapper = styled('div')`
  position: relative;
`;

const DropdownMenu = styled('div')`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  z-index: 300;
  min-width: 190px;
  padding: 6px 0;
`;

const DropdownItem = styled('button')`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.danger ? '#e53935' : '#333')};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }
`;

const BoardButtons = styled('div')`
  display: flex;
  flex-direction: row;
`;

const BoardBody = styled('div')`
  display: flex;
  overflow: visible;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

const NARROW_BREAKPOINT = 600;

const Board = ({
  activeBoards,
  id,
}) => {
  const leaderboardExercise = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardExercise,
  );
  const leaderboardForm = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardForm,
  );
  const leaderboardSettings = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].settings,
  );
  const isTemplate = useSelector(
    (state) => state.leaderboard.data.leaderboards[id]?.isTemplate || false,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isAthlete = currentUser?.admin === 0;
  const dispatch = useDispatch();

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Unread on purpose — its setter forces Board to re-render on every completed
  // reload so the loading bar's `reset: true` re-arms, since a successful reload
  // doesn't otherwise guarantee any of this component's selected state changes.
  const [, setReloadTick] = useState(0);

  const { autoLoad, loadTime } = leaderboardSettings;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < NARROW_BREAKPOINT);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useInterval(() => {
    dispatch(reloadLeaderboard(leaderboardForm, id))
      .then(() => setReloadTick((tick) => tick + 1))
      // A failed tick is already recorded via FETCH_LEADERBOARD_REJECTED inside the
      // thunk; there's nothing more to surface for a silent background refresh, and
      // the interval will just retry on the next cycle.
      .catch(() => {});
  }, !autoLoad ? null : loadTime.value);

  const BoardTimerAnimation = useSpring({
    width: !autoLoad ? '0%' : '100%',
    from: {
      width: '0%',
    },
    config: { duration: loadTime.value },
    reset: true,
  });

  const handleSetModalOpen = (modalOpen, modal, boardId) => {
    dispatch(setModalOpen(modalOpen, modal, boardId));
  };

  const openModal = (modal, boardId) => {
    handleSetModalOpen(true, modal, boardId);
  };

  const editBoard = () => {
    openModal('Edit Leaderboard', id);
  };

  const closeBoard = () => {
    dispatch(closeBoardAction(id));
  };

  const downloadBoard = () => {
    openModal('Export Leaderboard', id);
  };

  const boardSettings = () => {
    openModal('Leaderboard Settings', id);
  };

  const menuItems = [
    { icon: 'settings', label: 'Settings', action: boardSettings },
    { icon: 'download', label: 'Download', action: downloadBoard },
    ...(!isTemplate && !isAthlete ? [{ icon: 'save', label: 'Save as Template', action: () => openModal('Save as Template', id) }] : []),
    { icon: 'pencil', label: 'Edit', action: editBoard },
    {
      icon: 'remove', label: 'Remove', action: closeBoard, danger: true,
    },
  ];

  return (
    <BoardContainer ref={containerRef}>
      <animated.div
        style={BoardTimerAnimation}
      >
        <BoardLoadingBar />
      </animated.div>
      <BoardHeader>
        <ExerciseNameWrapper>
          <SubText>{leaderboardExercise}</SubText>
          <InfoIconWrapper>
            <TBTooltip
              displayColor='#444444'
              hoverContent={(
                <LeaderboardSummaryTable
                  category={leaderboardForm.category}
                  exerciseName={leaderboardExercise}
                  groups={leaderboardForm.groups}
                  mustBeInAllGroups={leaderboardForm.mustBeInAllGroups}
                  gender={leaderboardForm.gender}
                  rangeType={leaderboardForm.rangeType}
                  rangeDates={leaderboardForm.rangeDates}
                  compareTo={leaderboardForm.compareTo}
                  compareDates={leaderboardForm.compareDates}
                />
              )}
              icon='info'
              iconSize={10}
              id={`board-info-${id}`}
              place='bottom'
              styledPropsWrapper={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              tooltipBorder='1px solid #eee'
              tooltipOpacity={1}
              tooltipStyles={{
                background: 'white',
                color: '#444',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: '14px 16px',
                minWidth: '300px',
                maxWidth: '420px',
                whiteSpace: 'normal',
                lineHeight: '1.5',
                zIndex: 400,
              }}
            />
          </InfoIconWrapper>
        </ExerciseNameWrapper>
        {isNarrow ? (
          <MenuWrapper ref={menuRef}>
            <Button
              name=''
              icon='dots'
              iconColor='#444444'
              iconOnly
              noBorder
              type='button'
              onClick={() => setMenuOpen((prev) => !prev)}
            />
            {menuOpen && (
              <DropdownMenu>
                {menuItems.map(({
                  icon, label, action, danger,
                }) => (
                  <DropdownItem
                    key={label}
                    type='button'
                    danger={danger}
                    onClick={() => { action(); setMenuOpen(false); }}
                  >
                    <IcomoonReact iconSet={iconSet} size={14} icon={icon} color={danger ? '#e53935' : '#333'} />
                    {label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </MenuWrapper>
        ) : (
          <BoardButtons>
            <Button
              name=''
              icon='settings'
              iconColor='#979797'
              iconOnly
              noBorder
              onClick={boardSettings}
            />
            <Button
              name=''
              icon='download'
              iconColor='#979797'
              iconOnly
              noBorder
              onClick={downloadBoard}
            />
            {!isTemplate && !isAthlete && (
              <Button
                name=''
                icon='save'
                iconColor='#979797'
                iconOnly
                noBorder
                onClick={() => openModal('Save as Template', id)}
              />
            )}
            <Button
              name=''
              icon='pencil'
              iconColor='#979797'
              iconOnly
              noBorder
              onClick={editBoard}
            />
            <Button
              name=''
              icon='remove'
              iconColor='#979797'
              iconOnly
              noBorder
              onClick={closeBoard}
            />
          </BoardButtons>
        )}
      </BoardHeader>
      <BoardBody>
        <BoardData
          activeBoards={activeBoards}
          id={id}
        />
      </BoardBody>
    </BoardContainer>
  );
};

Board.propTypes = {
  activeBoards: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
};

export default Board;
